import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'
import { CommonTable } from '@/components/common'
import { chairpersonService } from '@/services'
import { formatUtcDate } from '@/utils/date'
import { DriveDonorsTabShell } from './DriveDonorsTabShell'
import { DonorsSelectionToolbar } from './DonorsSelectionToolbar'
import { DonorsExportButton } from './DonorsExportButton'
import { EligibilityBadge } from './EligibilityBadge'
import {
  DRIVE_DONORS_DATE_FORMAT,
  DRIVE_DONORS_EMAIL_SOURCE,
  DRIVE_DONORS_RECIPIENT_MODES,
  DRIVE_DONORS_SELECTED_RECIPIENT_ERROR,
  DRIVE_DONORS_SOURCE_TABS,
  DONOR_EXPORT_CONTACT_HEADERS,
  hasDriveDonorsSelectedRecipients,
  navigateToDriveDonorsEmailFlow,
} from './constants'
import { pickChairpersonDriveNavState } from '@/utils/chairpersonNavigation'

const ELIGIBILITY_CSV_HEADERS = [
  { key: 'eligibility_status', label: 'Eligibility for this drive' },
  { key: 'eligible_on', label: 'Eligible On' },
]

const SORT_FIELD_MAP = {
  donor_name: 'last_name',
  last_donation_at_account: 'last_donation_at_account',
}

export const AccountDonorsTab = ({ driveId, accountId }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [driveProcedureCount, setDriveProcedureCount] = useState(0)
  const [isPastDrive, setIsPastDrive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedRows, setSelectedRows] = useState([])
  const [allDonorsCount, setAllDonorsCount] = useState(null)
  const [allEligibleDonorsCount, setAllEligibleDonorsCount] = useState(null)
  const [loadingSendOptions, setLoadingSendOptions] = useState(false)
  const [emailFlowError, setEmailFlowError] = useState('')
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 25,
    sortBy: 'donor_name',
    sortDirection: 'asc',
  })

  const driveNavState = pickChairpersonDriveNavState(location.state)

  const startEmailFlow = useCallback(
    recipientMode => {
      const donorIds =
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
          ? selectedRows.map(row => String(row.donor_id)).filter(Boolean)
          : []

      if (
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED &&
        !hasDriveDonorsSelectedRecipients({ donorIds })
      ) {
        setEmailFlowError(DRIVE_DONORS_SELECTED_RECIPIENT_ERROR)
        return
      }

      setEmailFlowError('')
      navigateToDriveDonorsEmailFlow({
        navigate,
        driveId,
        accountId,
        donorIds,
        guestIds: [],
        sourceTab: DRIVE_DONORS_SOURCE_TABS.ACCOUNT_DONORS,
        recipientMode,
        driveNavState,
      })
    },
    [accountId, driveId, driveNavState, navigate, selectedRows]
  )

  const fetchDonors = useCallback(async () => {
    if (!driveId || !accountId) return
    setLoading(true)
    try {
      const sortBy =
        SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy ?? 'last_name'
      const response = await chairpersonService.getDriveDonorsAccountDonors(
        driveId,
        accountId,
        {
          page: pagination.page + 1,
          limit: pagination.perPage,
          sortBy,
          sortOrder: pagination.sortDirection?.toUpperCase() ?? 'ASC',
        }
      )
      const payload = response?.data ?? {}
      setRows(Array.isArray(payload.donors) ? payload.donors : [])
      setTotalRecords(Number(payload.total_count ?? 0))
      setDriveProcedureCount(Number(payload.drive_procedure_count ?? 0))
      setIsPastDrive(Boolean(payload.is_past_drive))
    } catch (error) {
      console.error('Error fetching account donors', error)
      setRows([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [driveId, accountId, pagination])

  useEffect(() => {
    fetchDonors()
  }, [fetchDonors])

  useEffect(() => {
    setSelectedRows([])
  }, [pagination.page, pagination.perPage, driveId])

  useEffect(() => {
    if (!driveId || !accountId) return undefined

    let cancelled = false

    const loadSendCounts = async () => {
      setLoadingSendOptions(true)
      try {
        const [allDonorsPreview, allEligiblePreview] = await Promise.all([
          chairpersonService.previewDriveDonorsEmailRecipients(
            driveId,
            accountId,
            {
              source_tab: DRIVE_DONORS_SOURCE_TABS.ACCOUNT_DONORS,
              source: DRIVE_DONORS_EMAIL_SOURCE,
              recipient_mode: DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS,
            }
          ),
          isPastDrive
            ? Promise.resolve(null)
            : chairpersonService.previewDriveDonorsEmailRecipients(
                driveId,
                accountId,
                {
                  source_tab: DRIVE_DONORS_SOURCE_TABS.ACCOUNT_DONORS,
                  source: DRIVE_DONORS_EMAIL_SOURCE,
                  recipient_mode:
                    DRIVE_DONORS_RECIPIENT_MODES.ALL_ELIGIBLE_DONORS,
                }
              ),
        ])

        if (!cancelled) {
          setAllDonorsCount(Number(allDonorsPreview?.data?.will_receive) || 0)
          setAllEligibleDonorsCount(
            allEligiblePreview
              ? Number(allEligiblePreview?.data?.will_receive) || 0
              : null
          )
        }
      } catch (error) {
        if (!cancelled) {
          setAllDonorsCount(null)
          setAllEligibleDonorsCount(null)
        }
      } finally {
        if (!cancelled) {
          setLoadingSendOptions(false)
        }
      }
    }

    loadSendCounts()

    return () => {
      cancelled = true
    }
  }, [accountId, driveId, isPastDrive])

  const tableData = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        donor_name: row.donor_name ?? 'N/A',
        donor_number: row.donor_number ?? 'N/A',
        last_donation_at_account: row.last_donation_at_account
          ? formatUtcDate(
              row.last_donation_at_account,
              DRIVE_DONORS_DATE_FORMAT
            )
          : 'N/A',
      })),
    [rows]
  )

  const selectedDonorIds = useMemo(
    () => selectedRows.map(row => String(row.donor_id)).filter(Boolean),
    [selectedRows]
  )

  const allSelected =
    tableData.length > 0 && selectedRows.length === tableData.length

  const handleToggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : [...tableData])
  }

  const csvHeaders = useMemo(
    () => [
      { key: 'donor_name', label: 'Donor' },
      { key: 'donor_number', label: 'Donor Number' },
      { key: 'last_donation_at_account', label: 'Last Donation' },
      ...(isPastDrive ? [] : ELIGIBILITY_CSV_HEADERS),
      ...DONOR_EXPORT_CONTACT_HEADERS,
    ],
    [isPastDrive]
  )

  const fetchExportData = useCallback(async () => {
    const sortBy =
      SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy ?? 'last_name'
    const response = await chairpersonService.getDriveDonorsAccountDonors(
      driveId,
      accountId,
      {
        forExport: 'true',
        sortBy,
        sortOrder: pagination.sortDirection?.toUpperCase() ?? 'ASC',
      }
    )
    const exportRows = Array.isArray(response?.data?.donors)
      ? response.data.donors
      : []

    return exportRows.map(row => ({
      donor_name: row.donor_name,
      donor_number: row.donor_number || 'N/A',
      last_donation_at_account: row.last_donation_at_account
        ? formatUtcDate(row.last_donation_at_account, DRIVE_DONORS_DATE_FORMAT)
        : 'N/A',
      ...(isPastDrive
        ? {}
        : {
            eligibility_status:
              row.eligibility?.status === 'eligible'
                ? 'Eligible for this drive'
                : 'Not eligible for this drive',
            eligible_on:
              row.eligibility?.status === 'not_eligible' &&
              row.eligibility?.next_eligibility_date
                ? formatUtcDate(
                    row.eligibility.next_eligibility_date,
                    DRIVE_DONORS_DATE_FORMAT
                  )
                : '',
          }),
      email: row.email || '',
      phone: row.phone || '',
      calling_preference: row.calling_preference || '',
      text_preference: row.text_preference || '',
      email_preference: row.email_preference || '',
    }))
  }, [
    driveId,
    accountId,
    isPastDrive,
    pagination.sortBy,
    pagination.sortDirection,
  ])

  const columns = useMemo(() => {
    const nextColumns = [
      { id: 'donor_name', label: 'Donor', sortable: true, minWidth: 180 },
      { id: 'donor_number', label: 'Donor Number', minWidth: 120 },
      {
        id: 'last_donation_at_account',
        label: 'Last Donation',
        sortable: true,
        minWidth: 140,
      },
    ]

    if (!isPastDrive) {
      nextColumns.push({
        id: 'eligibility',
        label: 'Eligibility for this drive',
        minWidth: 220,
        render: ({ row }) => (
          <EligibilityBadge
            eligibility={row.eligibility}
            showProcedureTooltip={driveProcedureCount > 1}
          />
        ),
      })
    }

    return nextColumns
  }, [driveProcedureCount, isPastDrive])

  return (
    <DriveDonorsTabShell
      statTitle={`Account Donors (${totalRecords})`}
      statSubtitle='Total donors (donated in last 24 months)'
      showEligibilityBanner={!isPastDrive}
      aboutTitle='About Account Donors'
      aboutText='This list includes donors associated with this account who have donated within the last 24 months. This list does not include donors who have not donated in the past 24 months.'
      selectionToolbar={
        <DonorsSelectionToolbar
          selectedCount={selectedDonorIds.length}
          selectionActionsDisabled={selectedDonorIds.length === 0}
          allSelected={allSelected}
          onToggleSelectAll={handleToggleSelectAll}
          onSendToSelected={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.SELECTED)
          }
          onSendToAllDonors={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS)
          }
          onSendToAllEligibleDonors={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.ALL_ELIGIBLE_DONORS)
          }
          onClearSelection={() => setSelectedRows([])}
          allDonorsCount={allDonorsCount}
          allEligibleDonorsCount={allEligibleDonorsCount}
          loadingSendOptions={loadingSendOptions}
          showEligibleOption={!isPastDrive}
        />
      }
      exportAction={
        <DonorsExportButton
          disabled={loading || !totalRecords}
          fetchExportData={fetchExportData}
          csvHeaders={csvHeaders}
          csvFilename={`Drive_Account_Donors_${driveId}.csv`}
        />
      }
      errorMessage={emailFlowError}
    >
      <CommonTable
        columns={columns}
        data={tableData}
        loading={loading}
        mode='server'
        totalItems={totalRecords}
        showSelect
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageSizeOptions={[10, 25, 50]}
      />
    </DriveDonorsTabShell>
  )
}

AccountDonorsTab.propTypes = {
  driveId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
